import { Link } from 'expo-router';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@fhs-legacy/universal-components';
import { IBackendCartEntries, IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { LoyaltyRbiChannel } from 'generated/graphql-gateway';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { focusableLabeledA11yProps } from 'utils/accessibility/focusable-labeled-props';

import { FireOrderBadge } from './fire-order-badge';
import {
  Col,
  FormattedDate,
  ItemNameContainer,
  Items,
  MoreItems,
  MoreItemsText,
  NumItems,
  OrderNumberWrapper,
  SubtitleContainer,
  Tile,
} from './styled';

interface IOrderProps {
  order: IHistoricalOrder;
}

const RestaurantOrder = ({ order }: IOrderProps) => {
  const { formatMessage } = useIntl();
  const createAriaLabel = useCreateOrderAriaLabel();

  const formattedDate = useMemo(
    () =>
      new Date(order.createdAt).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [formatMessage, order.createdAt]
  );

  const { itemCSV, numItems } = useMemo(() => {
    const cartEntries = order?.cart?.cartEntries ?? [];
    const csvForItem = cartEntries.map((entry: IBackendCartEntries) => entry.name).join(', ');
    const totalItemsOrdered = cartEntries.reduce((acc: number, item: IBackendCartEntries) => {
      if (item.lineId === 'discount-offer') {
        return acc;
      }
      return acc + item.quantity;
    }, 0);

    const itemsCount = `${totalItemsOrdered} ${(totalItemsOrdered === 1
      ? formatMessage({ id: 'item' })
      : formatMessage({ id: 'items' })
    ).toLowerCase()}`;

    return { itemCSV: csvForItem, numItems: itemsCount };
  }, [order, formatMessage]);

  // Show the first item and tell the user how many other items there is in the order
  const itemsArr = itemCSV.split(',');
  const remainingItems = itemsArr.length - 1;
  const renderMoreItems = () => (
    <Link
      href={{
        pathname: `/receipt/${order.loyaltyTransaction?.id}`,
        params: { back: '/account/orders', orderChannel: LoyaltyRbiChannel.RESTAURANT },
      }}
      asChild
    >
      <MoreItems>
        <MoreItemsText>{formatMessage({ id: 'moreItems' }, { remainingItems })}</MoreItemsText>
      </MoreItems>
    </Link>
  );

  const itemsSummaryLabel = useMemo(() => {
    const first = (itemCSV.split(',')[0] ?? '').trim();
    return `${numItems}, ${first}`;
  }, [itemCSV, numItems]);

  return (
    <>
      <Tile testID={`recent-order-${order.rbiOrderId}`}>
        <Col>
          <FireOrderBadge
            inProgress={order?.order?.inProgress ?? false}
            serviceMode={order.cart.serviceMode}
          />
          <OrderNumberWrapper />
          <Box {...focusableLabeledA11yProps(formattedDate)}>
            <FormattedDate importantForAccessibility="no-hide-descendants" variant="headerThree">
              {formattedDate}
            </FormattedDate>
          </Box>
          <ItemNameContainer>
            <Box {...focusableLabeledA11yProps(itemsSummaryLabel)}>
              <SubtitleContainer
                importantForAccessibility="no-hide-descendants"
                variant="headerFour"
              >
                <NumItems>{numItems}</NumItems>
              </SubtitleContainer>
              <Box importantForAccessibility="no-hide-descendants">
                <Items>{itemsArr[0]}</Items>
              </Box>
            </Box>
            {remainingItems > 0 && renderMoreItems()}
          </ItemNameContainer>
        </Col>
        <Col>
          <Box width="full">
            <ActionButton
              testID="recent-order-view-details"
              variant={ActionButtonVariants.OUTLINE}
              fullWidth
              _text={{ numberOfLines: 1 }}
              to={{
                pathname: `/receipt/${order.loyaltyTransaction?.id}`,
                params: { back: '/account/orders', orderChannel: LoyaltyRbiChannel.RESTAURANT },
              }}
              aria-label={createAriaLabel({
                buttonType: 'view',
                order,
              })}
            >
              {formatMessage({ id: 'viewDetails' })}
            </ActionButton>
          </Box>
        </Col>
      </Tile>
    </>
  );
};

export default RestaurantOrder;
